/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LogService, RbacService, UtilsService } from '../../../../../core/services';
import { UpdateModel } from '../../models';
import { UpdatesService } from '../../services';
import { WidgetBaseComponent } from '../widgetBase.component';
import { WidgetInterface } from '../../interfaces/widget.interface';
import { WidgetStateEnum } from '../../../../../core/enums';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-widget-sysupdates',
    templateUrl: './systemUpdates.widget.html',
    styleUrls: ['./systemUpdates.widget.scss', '../../../../../core/components/widget/widget.component.scss']
})

export class SystemUpdatesWidgetComponent extends WidgetBaseComponent implements WidgetInterface {
    /**
     *  Define our updates model.
     */
    public updates: UpdateModel = {
        majorVersion: [],
        packages: []
    };

    /**
     * Loading variable.
     */
    public loading: boolean;

    /**
     * Set our default state.
     */
    public state: WidgetStateEnum;

    /**
     * Store the total number of updates available
     */
    public count: number | string = 0;

    /**
     * Store permissions for button.
     */
    public permissionsLookup: any = {};

    constructor(
        private logService: LogService,
        private rbacService: RbacService,
        private route: ActivatedRoute,
        private router: Router,
        private updatesService: UpdatesService,
        utils: UtilsService
    ) {
        super(utils);
        this.loading = true;
        this.resetLocalVariables();
        this.registerButtons();
        this.getWidgetDetails();
    }

    /**
     * Reset any local variables.
     */
    public resetLocalVariables(): void {
        this.updates = <UpdateModel>{};
        this.state = WidgetStateEnum.OK;
    }

    /**
     * Get our widget details.
     */
    public getWidgetDetails(): void {
        this.updatesService.forceUpdateCheck();

        this.updatesService.updatesEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (updates: any): void => {
                    this.loading = false;
                    this.resetLocalVariables();
                    this.updates = updates;
                    this.updateCount(updates);
                },
                error: (error: ErrorResponseModel): void => {
                    this.loading = false;
                    this.logService.error(
                        'SystemUpdatesWidgetComponent: getWidgetDetails',
                        'Error getting update details from the updates service ' + error.message
                    );
                    this.count = 'N/A';
                }
            });
    }

    /**
     * Update the count variable.
     * @param updates
     * @private
     */
    private updateCount(updates: UpdateModel): void {
        this.count = updates.majorVersion.length + updates.packages.length;
    }

    /**
     * Required as part of the utils interface.
     */
    public socketListeners(): void {
    }

    /**
     * Navigate to a path relative to route.
     * @param url
     */
    public internalLink(url: string) {
        this.router.navigate([url], { relativeTo: this.route }).then(() => {
        });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'sys-updates-widget-link': [{ path: '/system/update', methods: ['PUT'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
